import type { BoardRow, BoardTask } from '../../services/devopsBoard.service';

export interface Filters {
  keyword: string;
  states: string[];
  assignees: string[];
  types: string[];
}

export const EMPTY_FILTERS: Filters = {
  keyword: '',
  states: [],
  assignees: [],
  types: [],
};

/** Null assignees are shown and filtered as "Unassigned", matching DevOps. */
export const UNASSIGNED = 'Unassigned';

export function assigneeOf(item: { assignedTo: string | null }): string {
  return item.assignedTo ?? UNASSIGNED;
}

export function filtersActive(f: Filters): boolean {
  return (
    f.keyword.trim() !== ''
    || f.states.length > 0
    || f.assignees.length > 0
    || f.types.length > 0
  );
}

function matchesKeyword(item: { id: number; title: string }, keyword: string): boolean {
  return `${item.id} ${item.title}`.toLowerCase().includes(keyword);
}

function tasksOf(row: BoardRow): BoardTask[] {
  return Object.values(row.children ?? {}).flat();
}

/** Drop the task cards a person filter excludes, without touching the input. */
function withFilteredTasks(row: BoardRow, filters: Filters): BoardRow {
  if (!filters.assignees.length) return row;

  const children: BoardRow['children'] = {};
  Object.entries(row.children ?? {}).forEach(([column, tasks]) => {
    children[column] = tasks.filter(t => filters.assignees.includes(assigneeOf(t)));
  });
  return { ...row, children };
}

/**
 * Which story rows to show, and which of their task cards.
 *
 * Person and keyword match against the story OR any of its tasks: a story owned by
 * someone else still belongs on my board when it carries one of my tasks. State and
 * type apply to the story itself — every child is a Task, and the columns already
 * convey task state.
 *
 * A person filter additionally hides other people's task cards. Filtering to myself
 * should show my work, not everyone's work on stories I happen to touch — the story
 * card stays so I can see what the task belongs to.
 */
export function filterRows(rows: BoardRow[], filters: Filters): BoardRow[] {
  const keyword = filters.keyword.trim().toLowerCase();

  return rows
    .filter(row => {
      if (filters.states.length && !filters.states.includes(row.state)) return false;
      if (filters.types.length && !filters.types.includes(row.type)) return false;

      const tasks = tasksOf(row);

      if (filters.assignees.length) {
        const mine = filters.assignees.includes(assigneeOf(row))
          || tasks.some(t => filters.assignees.includes(assigneeOf(t)));
        if (!mine) return false;
      }

      if (keyword) {
        const hit = matchesKeyword(row, keyword) || tasks.some(t => matchesKeyword(t, keyword));
        if (!hit) return false;
      }

      return true;
    })
    .map(row => withFilteredTasks(row, filters));
}

/** Everyone who appears on the board, as a story owner or a task assignee. */
export function allAssignees(rows: BoardRow[]): string[] {
  const names = new Set<string>();
  rows.forEach(row => {
    names.add(assigneeOf(row));
    tasksOf(row).forEach(t => names.add(assigneeOf(t)));
  });
  return Array.from(names).sort();
}
